import { generateUUID } from '@crm/shared/uuidv7';

const DEFAULT_BACKEND_API_URL = 'https://api.inmueble.red';
const DEMO_REQUEST_ENDPOINT = '/api/commercial/demo-requests';
const DEMO_REQUEST_TIMEOUT_MS = 10_000;
const HTTP_STATUS_OK = 200;
const HTTP_STATUS_CREATED = 201;

export interface SubmitCommercialDemoRequestInput {
  companyName: string;
  contactName: string;
  workEmail: string;
  phone?: string;
  employeeRange: string;
  planInterest: string;
  message?: string;
  locale?: string;
  source?: string;
  sourcePage?: string;
  honeypot?: string;
}

interface ErrorResponseBody {
  message?: string;
  error?: string;
}

export function resolveBackendApiBaseUrl(value?: string): string {
  const trimmed = value?.trim();
  if (trimmed && trimmed.length > 0) {
    return trimmed.replace(/\/+$/, '');
  }
  return DEFAULT_BACKEND_API_URL;
}

async function getResponseErrorMessage(response: Response): Promise<string> {
  try {
    const payload = (await response.json()) as ErrorResponseBody;
    if (payload.message && payload.message.trim().length > 0) {
      return payload.message;
    }
    if (payload.error && payload.error.trim().length > 0) {
      return payload.error;
    }
  } catch {
    // Ignore JSON parse errors and fall back to status text.
  }

  const statusText = response.statusText?.trim();
  if (statusText && statusText.length > 0) {
    return statusText;
  }

  return `Request failed with status ${response.status}`;
}

export async function submitCommercialDemoRequest(
  payload: SubmitCommercialDemoRequestInput,
  backendApiBaseUrl?: string
): Promise<void> {
  const controller = new AbortController();
  const idempotencyKey = generateUUID();
  const timeoutId = setTimeout(
    () => controller.abort(),
    DEMO_REQUEST_TIMEOUT_MS
  );

  try {
    const response = await fetch(
      `${resolveBackendApiBaseUrl(backendApiBaseUrl)}${DEMO_REQUEST_ENDPOINT}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      }
    );

    if (
      response.status !== HTTP_STATUS_CREATED &&
      response.status !== HTTP_STATUS_OK
    ) {
      const errorMessage = await getResponseErrorMessage(response);
      throw new Error(errorMessage);
    }
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }

    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}
